import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Text,
  Group,
  Stack,
  Select,
  Pagination,
  Modal,
  Alert,
  Card,
  Box,
  Menu,
  Image,
  Table,
  Tooltip,
  ActionIcon,
  SimpleGrid,
  Badge,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconFileText,
  IconInfoCircle,
  IconFilter,
  IconChevronDown,
  IconCheck,
  IconX,
  IconEye,
} from '@tabler/icons-react';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import PdfViewer from '../components/PdfViewer';
import PageContainer from '../components/ui/PageContainer';
import PageHeader from '../components/ui/PageHeader';
import SearchInput from '../components/ui/SearchInput';
import PageSkeleton from '../components/ui/PageSkeleton';
import { useSidebar } from '../contexts/SidebarContext';
import {
  materialEstudioService,
  MaterialEstudio,
  TIPOS_MATERIAL,
} from '../services/materialEstudioService';

const MaterialesPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const { collapsed: sidebarCollapsed } = useSidebar();
  // Alineado con Layout.tsx: por debajo de 1024px el sidebar es overlay y NO
  // ocupa espacio, así que el visor debe ir a pantalla completa sin descontar
  // su ancho. (Antes estaba en 768px y rompía en iPad grande ~1032px.)
  const isMobile = useMediaQuery('(max-width: 1023px)', false);
  // Estudiante: sidebar 220 expandido / 60 colapsado (0 en móvil)
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 60 : 220;

  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const colTipo = dark ? '#c8a8d6' : '#8a6aa0';
  const colEspecialidad = dark ? '#8aa9d6' : '#5c7bb0';

  const materialesQ = useQuery({
    queryKey: ['materiales', 'activos'],
    queryFn: () => materialEstudioService.listarActivos(),
    staleTime: 5 * 60 * 1000,
  });
  const materiales: MaterialEstudio[] = (materialesQ.data as MaterialEstudio[] | undefined) ?? [];

  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<string | null>(null);
  const [filterEspecialidad, setFilterEspecialidad] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [opened, { open, close }] = useDisclosure(false);
  const [selected, setSelected] = useState<MaterialEstudio | null>(null);

  // Anti-copia básico: deshabilita clic derecho dentro del visor
  useEffect(() => {
    if (!opened) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('[data-pdf-viewer]')) e.preventDefault();
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [opened]);

  const especialidades = useMemo(() => {
    const set = new Set<string>();
    materiales.forEach((m) => { if (m.especialidad) set.add(m.especialidad); });
    return Array.from(set).sort();
  }, [materiales]);

  const filtered = materiales.filter((m) => {
    const q = searchTerm.toLowerCase();
    const matchSearch =
      !q ||
      m.titulo.toLowerCase().includes(q) ||
      (m.descripcion || '').toLowerCase().includes(q) ||
      (m.tema || '').toLowerCase().includes(q) ||
      (m.autor || '').toLowerCase().includes(q) ||
      (m.tags || '').toLowerCase().includes(q);
    const matchTipo = !filterTipo || m.tipo === filterTipo;
    const matchEsp = !filterEspecialidad || m.especialidad === filterEspecialidad;
    return matchSearch && matchTipo && matchEsp;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const hayFiltrosActivos = Boolean(searchTerm || filterTipo || filterEspecialidad);

  const openMaterial = (m: MaterialEstudio) => {
    setSelected(m);
    open();
  };

  const tipoLabel = (v?: string | null) =>
    v ? (TIPOS_MATERIAL.find((t) => t.value === v)?.label || v) : '';

  const statsCards = [
    { label: 'Disponibles', value: materiales.length, icon: IconFileText },
    {
      label: 'Especialidades',
      value: especialidades.length,
      icon: IconFilter,
    },
  ];

  if (materialesQ.isLoading) {
    return <PageSkeleton variant="grid" count={3} />;
  }

  return (
    <PageContainer>
      <Stack gap="lg">
        <PageHeader
          title="Materiales"
          subtitle="Lee guías, protocolos y artículos en línea"
        />

        {/* Stats */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          {statsCards.map((s) => (
            <Card key={s.label} withBorder radius="lg" p="lg" style={cardStyle}>
              <Stack gap="sm" align="stretch">
                <s.icon size={20} color={textColor} style={{ flexShrink: 0 }} />
                <Box>
                  <Text
                    style={{
                      color: textColor,
                      fontSize: '1.6rem',
                      fontWeight: 700,
                      fontFamily: 'Space Grotesk, Inter, sans-serif',
                      lineHeight: 1.1,
                    }}
                  >
                    {s.value}
                  </Text>
                  <Text size="xs" ta="left" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                    {s.label}
                  </Text>
                </Box>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        {/* Filtros */}
        <Group gap="sm" align="center" wrap="wrap">
          <SearchInput
            placeholder="Buscar por título, tema, autor o tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 220 }}
          />

          <Menu shadow="md" width={220} position="bottom-start" withinPortal>
            <Menu.Target>
              <Button
                variant={filterTipo ? 'light' : 'default'}
                color="gray"
                radius="md"
                leftSection={<IconFilter size={16} />}
                rightSection={<IconChevronDown size={14} />}
              >
                {filterTipo ? tipoLabel(filterTipo) : 'Tipo'}
              </Button>
            </Menu.Target>
            <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
              <Menu.Item
                leftSection={!filterTipo ? <IconCheck size={14} /> : <Box style={{ width: 14 }} />}
                onClick={() => setFilterTipo(null)}
              >
                Tipo (todos)
              </Menu.Item>
              <Menu.Divider />
              {TIPOS_MATERIAL.map((t) => {
                const sel = filterTipo === t.value;
                return (
                  <Menu.Item key={t.value} onClick={() => setFilterTipo(t.value)} rightSection={sel ? <IconCheck size={14} /> : null}>
                    <Text size="sm" fw={sel ? 700 : 500}>{t.label}</Text>
                  </Menu.Item>
                );
              })}
            </Menu.Dropdown>
          </Menu>

          {especialidades.length > 0 && (
            <Menu shadow="md" width={260} position="bottom-start" withinPortal>
              <Menu.Target>
                <Button
                  variant={filterEspecialidad ? 'light' : 'default'}
                  color="gray"
                  radius="md"
                  leftSection={<IconFilter size={16} />}
                  rightSection={<IconChevronDown size={14} />}
                >
                  {filterEspecialidad || 'Especialidad'}
                </Button>
              </Menu.Target>
              <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
                <Menu.Item
                  leftSection={!filterEspecialidad ? <IconCheck size={14} /> : <Box style={{ width: 14 }} />}
                  onClick={() => setFilterEspecialidad(null)}
                >
                  Especialidad (todas)
                </Menu.Item>
                <Menu.Divider />
                {especialidades.map((e) => {
                  const sel = filterEspecialidad === e;
                  return (
                    <Menu.Item key={e} onClick={() => setFilterEspecialidad(e)} rightSection={sel ? <IconCheck size={14} /> : null}>
                      <Text size="sm" fw={sel ? 700 : 500}>{e}</Text>
                    </Menu.Item>
                  );
                })}
              </Menu.Dropdown>
            </Menu>
          )}

          {hayFiltrosActivos && (
            <Button variant="subtle" color="gray" onClick={() => { setSearchTerm(''); setFilterTipo(null); setFilterEspecialidad(null); }}>
              Limpiar
            </Button>
          )}
        </Group>

        {/* Tabla */}
        {materialesQ.error ? (
          <Alert icon={<IconInfoCircle size={16} />} color="red" radius="md">
            No se pudieron cargar los materiales. Intenta más tarde.
          </Alert>
        ) : filtered.length === 0 ? (
          <Card withBorder radius="lg" p="xl" style={cardStyle}>
            <Stack align="center" gap="md">
              <IconFileText size={48} style={{ opacity: 0.3, color: mutedColor }} />
              <Text style={{ color: mutedColor }}>
                {materiales.length === 0
                  ? 'Aún no hay materiales publicados.'
                  : 'Ningún material coincide con los filtros.'}
              </Text>
            </Stack>
          </Card>
        ) : (
          <Card withBorder radius="lg" p="lg" style={cardStyle}>
            <Table.ScrollContainer minWidth={720}>
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" withRowBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12, width: 64 }}>PDF</Table.Th>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Título</Table.Th>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Tipo</Table.Th>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Especialidad</Table.Th>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Año</Table.Th>
                    <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12, width: 90, textAlign: 'right' }}>Leer</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginated.map((m) => (
                    <Table.Tr key={m.id} style={{ cursor: 'pointer' }} onClick={() => openMaterial(m)}>
                      <Table.Td>
                        <Box
                          style={{
                            width: 44,
                            height: 56,
                            borderRadius: 6,
                            overflow: 'hidden',
                            background: dark ? '#0f172a' : '#e9e4dc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                          }}
                        >
                          {m.thumbnailUrl ? (
                            <Image src={m.thumbnailUrl} alt={m.titulo} fit="cover" w="100%" h="100%" />
                          ) : (
                            <IconFileText size={20} color={mutedColor} />
                          )}
                        </Box>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={m.titulo} multiline w={420} withArrow position="top-start">
                          <Box>
                            <Text size="xs" fw={600} ta="left" style={{ color: textColor }}>
                              {m.titulo}
                            </Text>
                            {m.autor && (
                              <Text size="xs" ta="left" style={{ color: mutedColor }}>
                                {m.autor}
                              </Text>
                            )}
                          </Box>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        {m.tipo ? (
                          <Badge variant="light" color="grape" size="sm" radius="sm">
                            {tipoLabel(m.tipo)}
                          </Badge>
                        ) : (
                          <Text size="xs" style={{ color: mutedColor }}>—</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={500} ta="left" style={{ color: colEspecialidad }}>
                          {m.especialidad || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" fw={500} ta="left" style={{ color: textColor }}>
                          {m.anio || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }} onClick={(e) => { e.stopPropagation(); openMaterial(m); }}>
                        <ActionIcon variant="subtle" color="gray" size="md" aria-label="Leer">
                          <IconEye size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <Group justify="space-between" align="center" mt="md" wrap="wrap" gap="sm">
              <Group gap="xs" align="center">
                <Text size="xs" style={{ color: mutedColor }}>
                  Mostrando {paginated.length} de {filtered.length.toLocaleString('es-MX')}
                </Text>
                <Select
                  size="xs"
                  w={92}
                  data={['10', '20', '50', '100']}
                  value={String(itemsPerPage)}
                  onChange={(v) => v && setItemsPerPage(Number(v))}
                  allowDeselect={false}
                  aria-label="Materiales por página"
                />
              </Group>
              {totalPages > 1 && (
                <Pagination value={page} onChange={setPage} total={totalPages} color="gray" size="sm" />
              )}
            </Group>
          </Card>
        )}
      </Stack>

      {/* Visor de PDF — solo lectura. zIndex 1300 para quedar SOBRE el sidebar (z 900).
          inner paddingLeft compensa el sidebar para centrar en el espacio útil.
          URL con #toolbar=0&navpanes=0 oculta la barra del visor del navegador
          (descargar/imprimir/abrir) en Chrome. */}
      <Modal
        opened={opened}
        onClose={close}
        withinPortal
        zIndex={1300}
        fullScreen={isMobile}
        size={isMobile ? undefined : `calc(100vw - ${sidebarWidth + 48}px)`}
        padding={0}
        radius={isMobile ? 0 : 'md'}
        shadow="xl"
        withCloseButton={false}
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
        styles={{
          inner: isMobile ? { padding: 0 } : { paddingLeft: sidebarWidth, paddingRight: 24 },
          content: {
            height: isMobile ? '100vh' : 'calc(100vh - 60px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          body: {
            padding: 0,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          },
        }}
      >
        {selected && (
          <>
            <Group
              justify="space-between"
              px="md"
              py="sm"
              wrap="nowrap"
              style={{
                borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                background: dark ? '#1e293b' : '#f7f3ee',
                flexShrink: 0,
              }}
            >
              <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                <IconFileText size={18} color={textColor} style={{ flexShrink: 0 }} />
                <Box style={{ minWidth: 0 }}>
                  <Text fw={600} truncate style={{ color: textColor, fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                    {selected.titulo}
                  </Text>
                  {selected.autor && (
                    <Text size="xs" truncate style={{ color: mutedColor }}>
                      {selected.autor}
                    </Text>
                  )}
                </Box>
              </Group>
              <ActionIcon variant="subtle" color="gray" onClick={close} aria-label="Cerrar">
                <IconX size={18} />
              </ActionIcon>
            </Group>
            <PdfViewer url={selected.pdfUrl} dark={dark} />
          </>
        )}
      </Modal>
    </PageContainer>
  );
};

export default MaterialesPage;
